import React from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  FileSpreadsheet, 
  CheckCircle2, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  ArrowUpRight,
  ArrowDownRight,
  Award 
} from 'lucide-react';
import { getCompletionColor } from './utils';

function MonthlyStatCard({ label, value, icon: Icon, iconBg, valueColor, trend }) {
  return (
    <Card className="p-5 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <div className="flex items-center gap-2 mt-1">
            <p className={`text-2xl font-bold ${valueColor || 'text-gray-900'}`}>
              {value}
            </p>
            {trend && (
              <div className={`flex items-center text-sm ${
                trend.direction === 'up' ? 'text-green-600' : 
                trend.direction === 'down' ? 'text-red-600' : 'text-gray-500'
              }`}>
                {trend.direction === 'up' && <ArrowUpRight className="w-4 h-4" />}
                {trend.direction === 'down' && <ArrowDownRight className="w-4 h-4" />}
                {trend.direction === 'stable' && <Minus className="w-4 h-4" />}
                <span>{Math.abs(trend.value)}%</span>
              </div>
            )}
          </div>
        </div>
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${valueColor?.replace('text-', '') || 'text-gray-600'}`} />
        </div>
      </div>
    </Card>
  );
}

export function MonthlyProductivity({ monthlyData }) {
  if (!monthlyData) return null;

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="w-5 h-5 text-green-600" />;
    if (direction === 'down') return <TrendingDown className="w-5 h-5 text-red-600" />;
    return <Minus className="w-5 h-5 text-gray-600" />;
  };

  const getTrendBg = (direction) => {
    if (direction === 'up') return 'bg-green-100';
    if (direction === 'down') return 'bg-red-100';
    return 'bg-gray-100';
  };

  return (
    <div className="space-y-6">
      {/* Current Month Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5 border border-gray-200 bg-gradient-to-br from-emerald-50 to-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Mês Atual</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {monthlyData.current_month?.month_name_pt || '-'}
              </p>
            </div>
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </Card>

        <MonthlyStatCard
          label="Tarefas do Mês"
          value={monthlyData.current_month?.tasks_due || 0}
          icon={FileSpreadsheet}
          iconBg="bg-blue-100"
          valueColor="text-blue-600"
        />

        <MonthlyStatCard
          label="Concluídas"
          value={monthlyData.current_month?.tasks_completed || 0}
          icon={CheckCircle2}
          iconBg="bg-green-100"
          valueColor="text-green-600"
        />

        <Card className="p-5 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Taxa de Conclusão</p>
              <div className="flex items-center gap-2 mt-1">
                <p className={`text-2xl font-bold ${getCompletionColor(monthlyData.current_month?.completion_rate || 0)}`}>
                  {monthlyData.current_month?.completion_rate || 0}%
                </p>
                {monthlyData.trend && (
                  <div className={`flex items-center text-sm ${
                    monthlyData.trend.direction === 'up' ? 'text-green-600' : 
                    monthlyData.trend.direction === 'down' ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {monthlyData.trend.direction === 'up' && <ArrowUpRight className="w-4 h-4" />}
                    {monthlyData.trend.direction === 'down' && <ArrowDownRight className="w-4 h-4" />}
                    {monthlyData.trend.direction === 'stable' && <Minus className="w-4 h-4" />}
                    <span>{Math.abs(monthlyData.trend.value)}%</span>
                  </div>
                )}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTrendBg(monthlyData.trend?.direction)}`}>
              {getTrendIcon(monthlyData.trend?.direction)}
            </div>
          </div>
        </Card>
      </div>

      {/* Year Summary */}
      <Card className="p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo Anual (Últimos 12 meses)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-3xl font-bold text-blue-600">{monthlyData.year_summary?.total_tasks || 0}</p>
            <p className="text-sm text-blue-700 mt-1">Total de Tarefas</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-3xl font-bold text-green-600">{monthlyData.year_summary?.completed_tasks || 0}</p>
            <p className="text-sm text-green-700 mt-1">Tarefas Concluídas</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-3xl font-bold text-purple-600">{monthlyData.year_summary?.average_completion_rate || 0}%</p>
            <p className="text-sm text-purple-700 mt-1">Taxa Média de Conclusão</p>
          </div>
        </div>
      </Card>

      {/* Monthly History Table */}
      <Card className="p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Histórico de Produtividade Mensal</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Mês</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Tarefas</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Concluídas</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Taxa</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Top Consultores</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.months_data?.map((month, index) => (
                <tr 
                  key={month.month} 
                  className={`border-b border-gray-100 hover:bg-gray-50 ${
                    index === monthlyData.months_data.length - 1 ? 'bg-emerald-50' : ''
                  }`}
                >
                  <td className="py-4 px-4">
                    <span className="font-medium text-gray-900">{month.month_name_pt}</span>
                    {index === monthlyData.months_data.length - 1 && (
                      <Badge className="ml-2 bg-emerald-100 text-emerald-700">Atual</Badge>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center font-medium">{month.tasks_due}</td>
                  <td className="py-4 px-4 text-center">
                    <Badge className="bg-green-100 text-green-700">
                      {month.tasks_completed}
                    </Badge>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Progress 
                        value={month.completion_rate} 
                        className="h-2 w-16"
                      />
                      <span className={`font-bold ${getCompletionColor(month.completion_rate)}`}>
                        {month.completion_rate}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex flex-wrap gap-1">
                      {month.top_consultants?.slice(0, 3).map((consultant, i) => (
                        <Badge 
                          key={consultant.name} 
                          className={`text-xs ${
                            i === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {i === 0 && <Award className="w-3 h-3 mr-1" />}
                          {consultant.name.split(' ')[0]} ({consultant.completion_rate}%)
                        </Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top Performers of Current Month */}
      {monthlyData.current_month?.top_consultants?.length > 0 && (
        <Card className="p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Destaques do Mês - {monthlyData.current_month.month_name_pt}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {monthlyData.current_month.top_consultants.map((consultant, index) => (
              <Card 
                key={consultant.name}
                className={`p-4 border ${
                  index === 0 ? 'border-yellow-300 bg-yellow-50' : 
                  index === 1 ? 'border-gray-300 bg-gray-50' : 
                  index === 2 ? 'border-orange-200 bg-orange-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    index === 0 ? 'bg-yellow-200' : 
                    index === 1 ? 'bg-gray-200' : 
                    index === 2 ? 'bg-orange-200' : 'bg-gray-100'
                  }`}>
                    {index === 0 ? (
                      <Award className="w-4 h-4 text-yellow-700" />
                    ) : (
                      <span className="font-bold text-sm">{index + 1}</span>
                    )}
                  </div>
                  <span className="font-semibold text-gray-900 text-sm truncate">
                    {consultant.name}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{consultant.completed}/{consultant.total}</span>
                  <span className={`font-bold ${getCompletionColor(consultant.completion_rate)}`}>
                    {consultant.completion_rate}%
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default MonthlyProductivity;
